import { createFileRoute, Link } from '@tanstack/react-router';
import type { RecurringEntry } from '@accounting/shared';
import { useRecurringEntries, useRunRecurringEntry, useSetRecurringActive } from '../../hooks/useRecurring';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate } from '../../lib/formatters';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/recurring/')({
  component: RecurringPage,
});

export function frequencyLabel(r: Pick<RecurringEntry, 'frequency' | 'interval' | 'dayOfMonth'>): string {
  const every = r.interval > 1 ? `every ${r.interval} ` : '';
  if (r.frequency === 'weekly') return `${every}${r.interval > 1 ? 'weeks' : 'weekly'}`;
  const unit = r.frequency === 'monthly' ? 'month' : r.frequency === 'quarterly' ? 'quarter' : 'year';
  const day = r.dayOfMonth ? ` (day ${r.dayOfMonth})` : '';
  return `${every}${r.interval > 1 ? unit + 's' : r.frequency}${day}`;
}

function RecurringPage() {
  const companyId = useActiveCompanyId();
  const { data: entries = [], isLoading, error } = useRecurringEntries(companyId);
  const run = useRunRecurringEntry(companyId);
  const setActive = useSetRecurringActive(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Recurring Entries</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Go to Companies</Link>
      </div>
    );
  }

  const runError =
    run.error instanceof ApiError
      ? (run.error.body as { error?: string } | null)?.error ?? 'Run failed'
      : run.error?.message;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Recurring Entries</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Templates that auto-generate journal entries on a schedule</p>
        </div>
        <Link to="/recurring/new" className="btn-primary" style={{ textDecoration: 'none' }}>+ New recurring entry</Link>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load recurring entries.</p>}
      {runError && <div className="field-error" style={{ marginBottom: 12 }}>{runError}</div>}

      {!isLoading && entries.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No recurring entries yet.</p>
          <Link to="/recurring/new" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Create your first template</Link>
        </div>
      )}

      {entries.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Schedule</th>
              <th>Next run</th>
              <th style={{ textAlign: 'right' }}>Runs</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((r) => (
              <tr key={r.id} style={{ opacity: r.isActive ? 1 : 0.55 }}>
                <td style={{ fontWeight: 500 }}>
                  {r.name}
                  {r.lastError && <span className="tag bad" style={{ marginLeft: 6 }} title={r.lastError}>last run failed</span>}
                </td>
                <td className="muted">{frequencyLabel(r)}</td>
                <td>{formatAccountingDate(r.nextRunDate)}{r.isDue && r.isActive ? <span className="tag warn" style={{ marginLeft: 6 }}>due</span> : null}</td>
                <td style={{ textAlign: 'right' }}>{r.runCount}{r.maxRuns != null ? ` / ${r.maxRuns}` : ''}</td>
                <td>{r.isActive ? <span className="tag ok">active</span> : <span className="tag">paused</span>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.isActive && (
                    <button
                      className="btn-secondary"
                      disabled={run.isPending}
                      onClick={() => run.mutate(r.id)}
                      style={{ marginRight: 6 }}
                      title="Generate the next scheduled entry now"
                    >
                      Run now
                    </button>
                  )}
                  <button
                    className="btn-secondary"
                    disabled={setActive.isPending}
                    onClick={() => setActive.mutate({ id: r.id, isActive: !r.isActive })}
                    style={{ marginRight: 6 }}
                  >
                    {r.isActive ? 'Pause' : 'Resume'}
                  </button>
                  <Link to="/recurring/$id" params={{ id: r.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>Edit</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
